import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { tourApi } from "@/lib/tourEntities";
import { assetApi, driverApi } from "@/lib/entities";
import { paginatedFetch, wrapListApi } from "@/lib/paginatedFetch";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip, StatusPill } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiError } from "@/lib/errors";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { TourHardDeleteModal } from "@/components/TourHardDeleteModal";
import { contractApi } from "@/lib/phase3Entities";
import { TourFormDialog } from "@/pages/tours/TourFormDialog";
import { TourContactsSection } from "@/pages/tours/TourContactsSection";
import { assetDisplay } from "@/lib/assetDisplay";
import { SetupProgress } from "@/components/SetupProgress";
import { customerApi } from "@/lib/entities";
import { Pencil } from "lucide-react";
import CoverageNeededCard, { hasCoverageNeeded } from "@/components/rc10l/CoverageNeededCard";
import MedicalExpiryAcknowledgmentModal from "@/components/rc10m/MedicalExpiryAcknowledgmentModal";
import MedicalRenewalBanner from "@/components/rc10m/MedicalRenewalBanner";
import DriverCannotBeAssignedDialog from "@/components/rc10s/DriverCannotBeAssignedDialog";
import {
  parseSaveErrorAsDiagnostic as rc10sParseSaveError,
} from "@/lib/driverAssignmentPreflight";
import PricingAndQuoteWorkspace from "@/pages/tours/tabs/PricingAndQuoteWorkspace";
import ContractsTab from "@/pages/tours/tabs/ContractsTab";
import PickupSheetsTab from "@/pages/tours/tabs/PickupSheetsTab";
import DepartureTab from "@/pages/tours/tabs/DepartureTab";
import RoutingTab from "@/pages/tours/tabs/RoutingTab";
import BillingTab from "@/pages/tours/tabs/BillingTab";
import HoldsTab from "@/pages/tours/tabs/HoldsTab";

// Wave 4B-6 · legacy role arrays retired. Every write control below is
// now gated on the canonical permission via `can(...)`. Admin auto-
// inherits every non-root permission at the resolver, so admin users
// receive full write access without any hand-listed role match.

const STATUSES = [
  "inquiry", "availability_review", "soft_hold", "first_hold", "second_hold",
  "contract_pending", "booked", "dispatch_ready", "dispatched",
  "active_tour", "completed", "billing", "closed", "cancelled",
];

export default function TourDetail() {
  const { id } = useParams();
  const { user, can, isSuperAdmin } = useAuth();
  const [tour, setTour] = useState(null);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [history, setHistory] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [tourContacts, setTourContacts] = useState([]);
  const [pricingLines, setPricingLines] = useState([]);
  const [routing, setRouting] = useState(null);

  const load = async () => {
    try {
      setTour(await tourApi.get(id));
      setAssets(await tourApi.listAssets(id));
      setDrivers(await tourApi.listDrivers(id));
      setHistory(await tourApi.statusHistory(id));
      tourApi.listContacts(id).then(setTourContacts).catch(() => setTourContacts([]));
      // Lightweight pricing check for setup progress
      import("@/lib/api").then(({ api }) =>
        api.get(`/tours/${id}/pricing`)
          .then((r) => setPricingLines(Array.isArray(r.data) ? r.data : []))
          .catch(() => setPricingLines([])),
      );
    } catch { toast.error("Tour not found or inaccessible"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (user?.role !== "driver") {
      // RC10p — iterate pages (RC10p backend now rejects
      // page_size>100 with 422).
      paginatedFetch(wrapListApi(assetApi.list), { pageSize: 100 })
        .then((res) => setAllAssets(res.items))
        .catch(() => {});
      paginatedFetch(wrapListApi(driverApi.list), { pageSize: 200 })
        .then((res) => setAllDrivers(res.items))
        .catch(() => {});
    }
  }, [user]);

  const changeStatus = async (to) => {
    try {
      await tourApi.changeStatus(id, to);
      toast.success(`Status → ${to}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Archive with blocker check for executed contracts (must be called
  // before any conditional return per React hook rules)
  const archive = useArchive({
    entityLabel: "Tour",
    apiArchive: tourApi.archive,
    description:
      "It will be removed from active tour lists. Assignments, holds, quotes, contracts, pickup sheets, and audit history are preserved.",
    checkBlockers: async (row) => {
      try {
        const cs = await contractApi.list(row.id);
        return (cs || [])
          .filter((c) => c.status === "executed")
          .map((c) => `contract ${c.contract_number || c.id.slice(0, 8)} (executed)`);
      } catch {
        return [];
      }
    },
    onSuccess: (row) => {
      toast.success(`Archived ${row.display_name}`);
      window.location.href = "/tours";
    },
  });

  const [editOpen, setEditOpen] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Correction Round 1 (2026-02) · Route-based initial tab. The
  // canonical deep-link `/tours/:id/pricing` resolves to
  // TourDetail with `activeTab="pricing"` even when the caller did
  // NOT pass `?tab=pricing`. Prevents the fixture URL in
  // `test_credentials.md` from falling through to the wildcard route
  // (which would redirect to /dashboard and appear as a session loop).
  const initialTab = (() => {
    const known = new Set([
      "overview","coaches","trailers","drivers","routing","pricing",
      "contracts","billing","pickup","departure","contacts",
      "holds","history",
    ]);
    const t = searchParams.get("tab");
    if (t && known.has(t)) return t;
    const path = (location.pathname || "").replace(/\/+$/, "");
    const suffix = path.split("/").pop();
    if (suffix && known.has(suffix)) return suffix;
    return "overview";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // Honor deep-links: if the ?tab= param changes while the page is mounted
  // (e.g. arriving via a Customer 360 View→ link), switch to that tab.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const openEdit = async () => {
    try {
      const cs = await customerApi.list({ active: true, page_size: 200 });
      setCustomersList(cs.results || []);
    } catch { setCustomersList([]); }
    setEditOpen(true);
  };

  if (!tour) return <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Tour Detail</SectionHeader><div className="text-steel text-sm">Loading tour…</div></PageShell>;

  const setupSteps = [
    { key: "client", label: "Client", complete: !!tour.customer_id },
    { key: "name", label: "Name", complete: !!tour.display_name },
    { key: "dates", label: "Dates", complete: !!(tour.start_date && tour.end_date) },
    { key: "contacts", label: "Contacts", complete: tourContacts.length > 0 },
    { key: "routing", label: "Routing", complete: !!(tour.pickup?.address || tour.dropoff?.address) },
    { key: "coach", label: "Coach", complete: assets.length > 0 },
    { key: "driver", label: "Driver", complete: drivers.length > 0 },
    { key: "pricing", label: "Pricing", complete: pricingLines.length > 0 },
  ];

  const canAssign = can("fleet.assign");
  const canWriteStatus = can("tours.edit");
  const canEditTour = can("tours.edit");
  const canWritePricing = can("pricing.edit");
  const canWriteQuotes = can("quotes.edit_draft") || can("quotes.generate");
  const canWriteContracts = can("contracts.create_draft") || can("contracts.execute");
  const canWriteHolds = can("tours.edit");
  const canWriteDocs = can("tours.edit");
  const canWriteRouting = can("routing.edit");
  const canWriteBilling = can("billing.edit");
  const canWriteItinerary = can("routing.edit") || can("tours.edit");
  const isOver = tour.status;

  return (
    <RoleGate permission="tours.view">
      <PageShell>
        {/* Correction Batch STEP 3 — Archived read-only banner. When
            `tour.is_archived` is true the operator lands on a snapshot
            of the record; every write control below is already gated
            on `!tour.is_archived` via `canWriteStatus`. This banner
            makes the state explicit so users understand why controls
            are dimmed. */}
        {tour.is_archived && (
          <div
            data-testid="archived-tour-banner"
            role="status"
            className="mb-3 border border-amber-hold bg-amber-hold/10 rounded-sm px-3 py-2 text-[12.5px] text-carbon flex items-center gap-2"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-amber-hold" aria-hidden="true" />
            <span className="font-semibold uppercase tracking-widest text-[10.5px] text-amber-hold">Archived Tour — Read-Only</span>
            <span className="text-steel">This tour is archived. Editing, status changes, and destructive actions are disabled. Restore from Archived Tours to modify.</span>
          </div>
        )}
        <PageHeader
          testid="page-tour-detail"
          backTo={{ label: "Back to Tours", to: "/tours" }}
          title={<span className="inline-flex items-center gap-3">
            {tour.display_name} {tour.is_demo && <DemoChip />}
          </span>}
          subtitle={<span className="inline-flex items-center gap-2 text-[12px] text-steel flex-wrap">
            <span className="font-mono">{tour.tour_number}</span>
            <span>·</span>
            <StatusPill status={tour.status} />
            <span>·</span>
            <span>
              {tour.start_date && tour.end_date
                ? `${tour.start_date} → ${tour.end_date}`
                : <span className="italic">Dates Not Added</span>}
            </span>
            <SetupProgress steps={setupSteps} testid="tour-setup-progress" />
          </span>}
          right={
            <div className="flex items-center gap-2 flex-wrap">
              {canWriteStatus && (
                <button
                  type="button"
                  onClick={openEdit}
                  data-testid="tour-edit-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white"
                >
                  <Pencil size={13} /> Edit Tour
                </button>
              )}
              {tour.status === "ready_to_book" && can && can("calendar.book_approved_tours") && (
                <button
                  type="button"
                  data-testid="tour-book-btn"
                  onClick={async () => {
                    try {
                      const { api } = await import("@/lib/api");
                      await api.post(`/tours/${tour.id}/book`);
                      toast.success("Tour booked on the Calendar.");
                      load();
                    } catch (e) {
                      const d = e?.response?.data?.detail;
                      const msg = typeof d === "string" ? d : (d?.message || "Booking failed.");
                      toast.error(msg);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-semibold bg-touring-lime text-carbon hover:bg-touring-lime/80"
                >
                  Book Tour
                </button>
              )}
              {canWriteStatus && (
                <select data-testid="tour-change-status"
                  onChange={(e) => e.target.value && changeStatus(e.target.value)}
                  defaultValue=""
                  className="border border-coach-blue text-coach-blue text-[12.5px] px-2 py-1.5 rounded-sm bg-white">
                  <option value="">Change status…</option>
                  {STATUSES.filter((s) => s !== isOver).map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </select>
              )}
              {canWriteStatus && !tour.is_archived && (
                <ActionMenu
                  testid={`tour-detail-actions-${tour.tour_number}`}
                  onArchive={() => archive.request(tour)}
                  onHardDelete={
                    isSuperAdmin ? () => setHardDeleteOpen(true) : undefined
                  }
                />
              )}
            </div>
          }
        />
        {editOpen && (
          <TourFormDialog
            open={editOpen}
            initial={tour}
            customers={customersList}
            onClose={() => setEditOpen(false)}
            onSaved={() => { setEditOpen(false); load(); }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
        <TourHardDeleteModal
          open={hardDeleteOpen}
          tour={tour}
          onCancel={() => setHardDeleteOpen(false)}
          onDeleted={() => {
            setHardDeleteOpen(false);
            window.location.href = "/tours";
          }}
          onArchived={() => {
            setHardDeleteOpen(false);
            window.location.href = "/tours";
          }}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0 w-full overflow-hidden">
          <TabsList className="bg-white border border-coach-silver w-full min-w-0 justify-start overflow-x-auto">
            {[
              ["overview","Overview"],["coaches","Coaches"],["trailers","Trailers"],
              ["drivers","Drivers"],["routing","Routing"],["pricing","Pricing & Quote"],
              ["contracts","Contracts"],["billing","Billing"],
              ["pickup","Pickup Sheets"],["departure","Departure"],
              ["contacts","Contacts"],
              ["holds","Holds"],["history","Status History"],
            ].map(([k,l]) => (
              <TabsTrigger key={k} value={k} data-testid={`tour-tab-${k}`}
                className="data-[state=active]:bg-coach-blue data-[state=active]:text-white">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 bg-white border border-coach-silver rounded-md p-4 grid md:grid-cols-2 gap-4 text-[13px]">
                <Row k="Customer" v={<Link to={`/customers/${tour.customer_id}`} className="text-coach-blue hover:underline">{tour.artist_display || tour.customer_id}</Link>} />
                <Row k="Tour Days" v={tour.tour_days || <span className="text-steel italic">Not Added</span>} />
                <Row k="Estimated Mileage" v={tour.estimated_mileage || <span className="text-steel italic">Not Added</span>} />
                <Row k="Coaches assigned" v={assets.length || <span className="text-steel italic">Not Assigned</span>} />
                <Row k="Drivers assigned" v={drivers.length || <span className="text-steel italic">Not Assigned</span>} />
                <Row k="Notes" v={<span className="text-carbon">{tour.notes || <span className="text-steel italic">None</span>}</span>} />
              </div>
              <PricingStatusCard tourId={id} tour={tour}
                onOpen={() => setActiveTab("pricing")} />
            </div>
            <WorkflowHubActions
              tour={tour}
              assets={assets}
              drivers={drivers}
              allAssets={allAssets}
              canAssign={canAssign}
              canQuote={canWriteQuotes}
              onGo={setActiveTab}
            />
          </TabsContent>

          <TabsContent value="coaches" className="mt-4">
            <AssignmentPanel kind="asset" tourId={id} tour={tour} rows={assets.filter((a) => {
              const asset = allAssets.find((x) => x.id === a.asset_id);
              return !asset || asset.asset_type !== "trailer";
            })} choices={allAssets.filter((a) => a.asset_type !== "trailer")}
            canWrite={canAssign} onChange={load} user={user} />
          </TabsContent>
          <TabsContent value="trailers" className="mt-4">
            <AssignmentPanel kind="asset" tourId={id} tour={tour} rows={assets.filter((a) => {
              const asset = allAssets.find((x) => x.id === a.asset_id);
              return asset && asset.asset_type === "trailer";
            })} choices={allAssets.filter((a) => a.asset_type === "trailer")}
            canWrite={canAssign} onChange={load} user={user} />
          </TabsContent>
          <TabsContent value="drivers" className="mt-4">
            <AssignmentPanel kind="driver" tourId={id} tour={tour} rows={drivers} choices={allDrivers}
            canWrite={canAssign} onChange={load} user={user} />
          </TabsContent>
          <TabsContent value="routing" className="mt-4">
            <RoutingTab tourId={id} canWrite={canWriteRouting} />
          </TabsContent>
          <TabsContent value="pricing" className="mt-4">
            <PricingAndQuoteWorkspace
              tour={tour}
              assets={assets}
              drivers={drivers}
              allAssets={allAssets}
              canWritePricing={canWritePricing}
              canWriteQuotes={canWriteQuotes}
            />
          </TabsContent>
          <TabsContent value="contracts" className="mt-4">
            <ContractsTab tourId={id}
              customerId={tour?.customer_id}
              canWrite={canWriteContracts}
              isSuperAdmin={user?.role === "super_admin"} />
          </TabsContent>
          <TabsContent value="billing" className="mt-4">
            <BillingTab tourId={id} />
          </TabsContent>
          <TabsContent value="pickup" className="mt-4 min-w-0">
            <PickupSheetsTab tourId={id} drivers={drivers}
              allDrivers={allDrivers} tourAssets={assets} allAssets={allAssets}
              targetAssignmentId={searchParams.get("da")}
              canWrite={can("departure.pickup_sheets")} />
          </TabsContent>
          <TabsContent value="departure" className="mt-4 min-w-0">
            <DepartureTab tourId={id} drivers={drivers}
              canWrite={can("departure.edit_checklist")} />
          </TabsContent>
          <TabsContent value="contacts" className="mt-4">
            <TourContactsSection
              tour={tour}
              canWrite={canEditTour}
            />
          </TabsContent>
          <TabsContent value="holds" className="mt-4">
            <HoldsTab
              tourId={id}
              canWrite={canWriteHolds}
              userRole={user?.role}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md" data-testid="tour-status-history">
              {history.length === 0 && <div className="p-4 text-[12.5px] text-steel">No transitions yet.</div>}
              {history.map((h) => (
                <div key={h.id} data-testid={`tour-history-${h.id}`}
                  className="px-4 py-2 border-b border-coach-silver/60 last:border-0 flex items-center gap-3 text-[13px]">
                  <StatusPill status={h.from_status || "unknown"} />
                  <span className="text-steel text-[11px]">→</span>
                  <StatusPill status={h.to_status} />
                  <span className="ml-4 text-carbon">{h.reason || <em className="text-steel">no reason</em>}</span>
                  <span className="ml-auto text-[11px] text-steel">{h.effective_at?.replace("T"," ").slice(0,16)}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </PageShell>
    </RoleGate>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0">
    <div className="text-[11px] uppercase tracking-wide text-steel">{k}</div>
    <div className="text-[13px] text-carbon">{v}</div>
  </div>
);

function AssignmentPanel({ kind, tourId, tour, rows, choices, canWrite, onChange, user }) {
  const [choiceId, setChoiceId] = useState("");
  const [startDate, setStartDate] = useState(tour.start_date);
  const [endDate, setEndDate] = useState(tour.end_date);
  const [driverRole, setDriverRole] = useState("primary_driver");
  const [override, setOverride] = useState("");
  const [conflict, setConflict] = useState(null);
  // RC10m — pending Medical Card ack state when server returns 409
  // `medical_expiry_acknowledgment_required`.
  const [medicalAck, setMedicalAck] = useState(null);
  const [medicalBusy, setMedicalBusy] = useState(false);
  // RC10s Pass 3 · structured diagnostic dialog for driver assignment
  // save failures. Replaces the generic toast fallback on this surface
  // so AssignmentPanel renders the same multi-reason modal as every
  // other assignment surface (BusGroupedView, DriverPickerModal, etc).
  const [rc10sDiagnostic, setRc10sDiagnostic] = useState(null);
  const [rc10sSelected, setRc10sSelected] = useState(null);

  const attach = async (withOverride = false, extra = {}) => {
    if (!choiceId) return toast.error("Select a resource");
    const payload = kind === "asset"
      ? { asset_id: choiceId, start_date: startDate, end_date: endDate }
      : { driver_id: choiceId, start_date: startDate, end_date: endDate, role: driverRole };
    if (withOverride) payload.override_reason = override;
    Object.assign(payload, extra);
    try {
      if (kind === "asset") await tourApi.addAsset(tourId, payload);
      else await tourApi.addDriver(tourId, payload);
      toast.success("Attached");
      setConflict(null);
      setMedicalAck(null);
      setRc10sDiagnostic(null);
      setOverride("");
      onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "medical_expiry_acknowledgment_required") {
        // RC10m — present full verbatim wording in the modal before retry
        const driverRow = choices.find((c) => c.id === choiceId) || {};
        setMedicalAck({
          driverName: driverRow.display_name || choiceId,
          roleLabel: (driverRole || "primary_driver").replace(/_/g, " "),
          medicalExpiration: detail.medical_card_expiration,
          tourName: tour.display_name || tour.tour_number || "this Tour",
        });
      } else if (kind === "driver" && (
        detail?.code === "driver_ineligible" ||
        detail?.code === "driver_needs_acknowledgement" ||
        detail?.code === "duplicate" ||
        detail?.code === "primary_as_co_driver" ||
        Array.isArray(detail?.blocking_reasons)
      )) {
        // RC10s Pass 3 — route assignment-specific 409/422 into the
        // shared "Driver Cannot Be Assigned" dialog. Carries the
        // same `blocking_reasons[] + warnings[] + correlation_id`
        // envelope and contextual action buttons used elsewhere.
        const driverRow = choices.find((c) => c.id === choiceId) || {};
        const diag = rc10sParseSaveError(e);
        setRc10sSelected({ id: choiceId, display_name: driverRow.display_name || choiceId });
        setRc10sDiagnostic(diag);
      } else if (detail?.code && detail?.conflicts) {
        setConflict(detail);
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const confirmMedicalAck = async () => {
    setMedicalBusy(true);
    try {
      await attach(false, { medical_expiry_acknowledged: true });
    } finally {
      setMedicalBusy(false);
    }
  };

  const remove = async (aid) => {
    try {
      if (kind === "asset") await tourApi.removeAsset(tourId, aid);
      else await tourApi.removeDriver(tourId, aid);
      onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 space-y-4">
      {canWrite && (
        <div className="grid md:grid-cols-5 gap-2 items-end" data-testid={`${kind}-add-form`}>
          <label className="block col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">{kind === "asset" ? "Asset" : "Driver"}</div>
            <select data-testid={`${kind}-choice`} value={choiceId} onChange={(e) => setChoiceId(e.target.value)}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white">
              <option value="">— Choose —</option>
              {choices
                .filter((c) => {
                  // F3 · Exclude quit/terminated drivers from assignment
                  // selectors. Assets not affected.
                  if (kind !== "driver") return true;
                  const s = c.employment_status || "active";
                  return s !== "quit" && s !== "terminated";
                })
                .map((c) => (
                <option key={c.id} value={c.id}>
                  {kind === "asset"
                    ? assetDisplay(c)
                    : `${c.display_name} (${c.driver_number})${(c.employment_status === "on_hold") ? "  ⚠ On Hold" : ""}`}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">Start</div>
            <input data-testid={`${kind}-start`} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]" />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">End</div>
            <input data-testid={`${kind}-end`} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]" />
          </label>
          {kind === "driver" ? (
            <label className="block">
              <div className="text-[11px] uppercase tracking-wide text-steel mb-1">Role</div>
              <select data-testid="driver-role-select" value={driverRole} onChange={(e) => setDriverRole(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white">
                {["primary_driver", "co_driver", "relief_driver"].map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                ))}
              </select>
            </label>
          ) : <div />}
          <button
            type="button"
            data-testid={`${kind}-attach`}
            onClick={() => attach(false)}
            disabled={!choiceId}
            aria-label={`Attach ${kind}`}
            className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm hover:bg-deep-road-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-coach-blue focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed">
            Attach
          </button>
        </div>
      )}

      {conflict && (
        <div data-testid="conflict-banner" className="border border-amber-hold bg-amber-hold/15 rounded-sm p-4">
          <div className="font-medium text-carbon">Conflict detected — {conflict.conflicts?.length || 0} issue(s)</div>
          <div className="text-[12px] text-steel mb-2">{conflict.message}</div>
          <ul className="text-[12.5px] text-carbon list-disc pl-5 mb-3">
            {(conflict.conflicts || []).map((c, i) => (
              <li key={i} data-testid={`conflict-item-${i}`}>
                {c.kind === "status" ? (<>Asset status <strong>{c.asset_status}</strong> blocks {c.overlap_start} → {c.overlap_end}</>) :
                 c.kind === "hold" ? (<>Existing {c.hold_level} hold overlaps {c.overlap_start} → {c.overlap_end}</>) :
                 (<>Overlap with <strong>{c.conflicting_tour_number}</strong> &quot;{c.conflicting_tour_display_name}&quot; ({c.overlap_start} → {c.overlap_end})</>)}
              </li>
            ))}
          </ul>
          {user?.role === "super_admin" ? (
            <div className="flex items-center gap-2">
              <input data-testid="override-reason" value={override} onChange={(e) => setOverride(e.target.value)}
                placeholder="Reason for override…" className="flex-1 border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]" />
              <button data-testid="override-submit" disabled={!override}
                onClick={() => attach(true)}
                className="bg-critical-red text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm disabled:opacity-40">
                Override with reason
              </button>
            </div>
          ) : (
            <div className="text-[12px] text-steel">Contact Operations to resolve.</div>
          )}
        </div>
      )}

      <table className="w-full text-[13px]">
        <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
          <tr>
            <th className="text-left px-3 py-2">{kind === "asset" ? "Asset" : "Driver"}</th>
            {kind === "driver" && <th className="text-left px-3 py-2 w-32">Role</th>}
            <th className="text-left px-3 py-2 w-52">Dates</th>
            <th className="text-left px-3 py-2 w-40">Override</th>
            <th className="w-16" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const label = kind === "asset"
              ? (assetDisplay(choices.find((c) => c.id === r.asset_id)) || r.asset_id.slice(0, 8))
              : (choices.find((c) => c.id === r.driver_id)?.display_name || r.driver_id.slice(0, 8));
            const showCoverage = kind === "driver" && hasCoverageNeeded(r);
            const showMedicalBanner = kind === "driver" && r.medical_expiry_warning_active === true;
            return (
              <React.Fragment key={r.id}>
                {showMedicalBanner && (
                  <tr data-testid={`rc10m-banner-row-${r.id}`}>
                    <td colSpan={5} className="px-3 pt-2 pb-0">
                      <MedicalRenewalBanner assignment={r} driverLabel={label} />
                    </td>
                  </tr>
                )}
                {showCoverage && (
                  <tr data-testid={`coverage-row-${r.id}`}>
                    <td colSpan={5} className="px-3 pt-2 pb-0">
                      <CoverageNeededCard
                        tour={tour}
                        assignment={r}
                        driverLabel={label}
                        onChange={onChange}
                      />
                    </td>
                  </tr>
                )}
                <tr data-testid={`${kind}-assn-${r.id}`} className="border-t border-coach-silver/60">
                <td className="px-3 py-2 text-carbon">{label}</td>
                {kind === "driver" && <td className="px-3 py-2 text-steel">{r.role?.replace(/_/g, " ")}</td>}
                <td className="px-3 py-2 text-steel">{r.start_date} → {r.end_date}</td>
                <td className="px-3 py-2 text-critical-red text-[11.5px]">{r.override_reason || ""}</td>
                <td className="px-3 py-2 text-right">
                  {canWrite && (
                    <button data-testid={`${kind}-remove-${r.id}`} onClick={() => remove(r.id)}
                      className="text-critical-red hover:underline text-[12px]">Remove</button>
                  )}
                </td>
                </tr>
              </React.Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-6 text-center text-steel">Nothing assigned yet.</td></tr>
          )}
        </tbody>
      </table>
      {medicalAck && (
        <MedicalExpiryAcknowledgmentModal
          driverName={medicalAck.driverName}
          roleLabel={medicalAck.roleLabel}
          medicalExpiration={medicalAck.medicalExpiration}
          tourName={medicalAck.tourName}
          busy={medicalBusy}
          onCancel={() => setMedicalAck(null)}
          onConfirm={confirmMedicalAck}
        />
      )}
      <DriverCannotBeAssignedDialog
        open={!!rc10sDiagnostic}
        diagnostic={rc10sDiagnostic}
        driverId={rc10sSelected?.id}
        tourId={tourId}
        driverName={rc10sSelected?.display_name}
        onClose={() => setRc10sDiagnostic(null)}
        onFindReplacement={() => setRc10sDiagnostic(null)}
        onAcknowledgeWarning={async () => {
          setRc10sDiagnostic(null);
          await attach(false, { warnings_acknowledged: true });
        }}
      />
    </div>
  );
}


// ---------------- Pricing Status card (Overview) ----------------
function PricingStatusCard({ tourId, tour, onOpen }) {
  const [summary, setSummary] = useState(null);
  const [latestQuote, setLatestQuote] = useState(null);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const [s, qs] = await Promise.all([
          import("@/lib/api").then(({ api }) => api.get(`/tours/${tourId}/pricing/summary`).then((r) => r.data)),
          import("@/lib/api").then(({ api }) => api.get(`/tours/${tourId}/quotes`).then((r) => r.data)),
        ]);
        if (!stop) { setSummary(s); setLatestQuote((qs || [])[0] || null); }
      } catch { /* noop — read-only card */ }
    })();
    return () => { stop = true; };
  }, [tourId]);
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    return Number.isNaN(n) ? String(v) : n.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };
  const latestLabel = latestQuote
    ? `v${latestQuote.version} ${latestQuote.status}`
    : "Not Generated";
  return (
    <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="pricing-status-card">
      <div className="text-[10.5px] uppercase tracking-widest text-steel mb-3 font-medium">Pricing Status</div>
      <div className="space-y-2 text-[13px]">
        <RowMini k="Billed Days"   v={tour.billed_days ?? tour.tour_days ?? "—"} />
        <RowMini k="Estimated Miles" v={tour.estimated_mileage ?? "—"} />
        <RowMini k="Enabled Items" v={summary?.line_count ?? "—"} />
        <RowMini k="Tour Budget"   v={fmt(summary?.total_tour_budget)} strong />
        <RowMini k="Latest Quote"  v={<span data-testid="pricing-status-latest-quote">{latestLabel}</span>} />
      </div>
      <button
        type="button"
        onClick={onOpen}
        data-testid="pricing-status-open"
        className="mt-4 w-full inline-flex items-center justify-center gap-1 h-9 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white"
      >
        Open Pricing & Quote →
      </button>
    </div>
  );
}

const RowMini = ({ k, v, strong }) => (
  <div className="flex items-baseline justify-between border-b border-coach-silver/50 pb-1 last:border-0 last:pb-0">
    <span className="text-[11px] uppercase tracking-wide text-steel">{k}</span>
    <span className={`tabular ${strong ? "text-deep-road-navy font-display text-[16px]" : "text-carbon"}`}>{v}</span>
  </div>
);

// ---------------- Workflow Hub Actions ----------------
// Wave 4B-5 P1 · Surface the four highest-value next-actions from the
// Tour Detail Overview so operators do not have to hunt through tabs to
// build a tour. Each button is a tab-switch shortcut — it reuses the
// existing assignment / pricing tabs (no new backend logic). Gating
// mirrors the tab-level gating already applied further down this file.
function WorkflowHubActions({ tour, assets, drivers, allAssets, canAssign, canQuote, onGo }) {
  const coachCount = assets.filter((a) => {
    const asset = allAssets.find((x) => x.id === a.asset_id);
    return !asset || asset.asset_type !== "trailer";
  }).length;
  const trailerCount = assets.filter((a) => {
    const asset = allAssets.find((x) => x.id === a.asset_id);
    return asset && asset.asset_type === "trailer";
  }).length;
  const driverCount = drivers.length;

  return (
    <div className="mt-4 bg-white border border-coach-silver rounded-md p-4" data-testid="tour-workflow-hub">
      <div className="text-[10.5px] uppercase tracking-widest text-steel mb-3 font-medium">Build This Tour</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <HubAction
          testid="hub-assign-coach"
          disabled={!canAssign}
          label={coachCount ? `Coaches (${coachCount})` : "+ Assign Coach"}
          hint={coachCount ? "Manage coach assignments" : "Attach a coach to this tour"}
          onClick={() => onGo("coaches")}
        />
        <HubAction
          testid="hub-assign-trailer"
          disabled={!canAssign}
          label={trailerCount ? `Trailers (${trailerCount})` : "+ Assign Trailer"}
          hint={trailerCount ? "Manage trailer assignments" : "Attach a trailer if needed"}
          onClick={() => onGo("trailers")}
        />
        <HubAction
          testid="hub-assign-driver"
          disabled={!canAssign}
          label={driverCount ? `Drivers (${driverCount})` : "+ Assign Driver"}
          hint={driverCount ? "Manage crew assignments" : "Assign primary / co-driver"}
          onClick={() => onGo("drivers")}
        />
        <HubAction
          testid="hub-create-quote"
          disabled={!canQuote}
          label="+ Create Quote"
          hint="Open Pricing & Quote workspace"
          onClick={() => onGo("pricing")}
          primary
        />
      </div>
      {!canAssign && !canQuote && (
        <div className="mt-3 text-[11.5px] text-steel italic">
          You have view-only access on this tour.
        </div>
      )}
    </div>
  );
}

const HubAction = ({ testid, label, hint, onClick, disabled, primary }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`text-left border rounded-sm px-3 py-2 transition-colors ${
      disabled
        ? "border-coach-silver/60 bg-road-white text-steel cursor-not-allowed"
        : primary
        ? "border-coach-blue bg-coach-blue text-white hover:bg-deep-road-navy"
        : "border-coach-silver bg-white text-carbon hover:border-coach-blue hover:bg-road-white"
    }`}
  >
    <div className="text-[12.5px] font-semibold">{label}</div>
    <div className={`text-[10.5px] ${disabled ? "text-steel" : primary ? "text-white/80" : "text-steel"}`}>{hint}</div>
  </button>
);
